import { useState } from "react";
import axiosInstance from "../common/axiosInstance";

export interface UploadMeta {
  premiseId?: string;
}

const BUCKET_NAME = "dev-kc-repo";
const REGION = "ap-south-1";

export function useS3Upload() {
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFileToS3 = async (
    file: File,
    metaOrPremiseId?: UploadMeta | string
  ): Promise<string> => {
    if (!file) {
      setError("No file selected");
      throw new Error("No file selected");
    }

    let meta: UploadMeta = {};
    if (typeof metaOrPremiseId === "string") {
      meta = { premiseId: metaOrPremiseId };
    } else if (metaOrPremiseId) {
      meta = metaOrPremiseId;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const contentType = file.type || "application/octet-stream";
      const paramsObj: Record<string, string> = {
        filename: file.name,
        contentType,
        fileSize: String(file.size),
      };

      if (meta.premiseId) {
        paramsObj.premiseId = meta.premiseId;
      }

      const { data } = await axiosInstance.get("/api/s3/presign-upload", { params: paramsObj });

      const { url, key } = data;

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload to S3 failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error("Upload to S3 failed (network/CORS)"));
        xhr.send(file);
      });

      setProgress(100);
      return key;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Upload failed";
      setError(message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return { uploadFileToS3, progress, uploading, error };
}

export async function getS3SignedUrl(key: string, contentType?: string): Promise<string> {
  const params: Record<string, string> = { key };
  if (contentType) {
    params.contentType = contentType;
  }
  const res = await axiosInstance.get(`/api/s3/presign-download`, { params });
  return res.data.url;
}

export function S3TempUrl() {
  const getsignedurl = async (fileName: string, type?: string): Promise<string> => {
    try {
      return await getS3SignedUrl(fileName, type);
    } catch (err: any) {
      return fileName;
    }
  };

  return { getsignedurl };
}

export const getSignedUrlForS3 = (urlOrKey: string): string => {
  if (!urlOrKey) return "";
  if (urlOrKey.startsWith("http://") || urlOrKey.startsWith("https://")) {
    return urlOrKey;
  }
  let key = urlOrKey;
  if (urlOrKey.includes("amazonaws.com/")) {
    key = urlOrKey.split("amazonaws.com/")[1];
  }
  return `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${encodeURIComponent(key)}`;
};

export const fetchSignedUrlForS3 = async (urlOrKey: string): Promise<string> => {
  if (!urlOrKey) return "";
  try {
    return await getS3SignedUrl(urlOrKey);
  } catch (err) {
    return getSignedUrlForS3(urlOrKey);
  }
};

export async function deleteS3File(key: string): Promise<void> {
  try {
    await axiosInstance.delete("/api/s3/delete", {
      data: { key },
    });
  } catch (err: any) {
    const message = err.response?.data?.message || err.message || "Failed to delete file";
    throw new Error(message);
  }
}

export default useS3Upload;